'use client'

import { CardWebcam, Container, Section, SectionHeader } from '@/ui/components'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import { memo, Suspense, useEffect } from 'react'
import { SectionWebcamItem, SectionWebcamsProps } from './type'

const SectionWebcams = ({ data }: SectionWebcamsProps) => {
  const c = data?.content
  const total = c?.items?.total

  useEffect(() => {
    // Lightbox CSS is only needed once the lightbox opens (on click), so load
    // it lazily instead of shipping it in the render-blocking CSS.
    void import('photoswipe/style.css')

    let lightboxes: PhotoSwipeLightbox[] = []

    total?.forEach((item: SectionWebcamItem) => {
      const lightbox = new PhotoSwipeLightbox({
        gallery: `#gallery-gallery`,
        children: 'a',
        pswpModule: () => import('photoswipe'),
        loop: false,
        showHideAnimationType: 'fade'
      })

      lightbox.on('uiRegister', function () {
        lightbox?.pswp?.ui?.registerElement({
          name: 'custom-caption',
          order: 9,
          isButton: false,
          appendTo: 'root',
          html: 'Caption text',
          onInit: (el, _pswp) => {
            lightbox?.pswp?.on('change', () => {
              const currSlideElement = lightbox?.pswp?.currSlide?.data?.element
              let captionHTML = ''
              if (currSlideElement) {
                const hiddenCaption = currSlideElement.querySelector(
                  '.hidden-caption-content'
                )
                if (hiddenCaption) {
                  // get caption from element with class hidden-caption-content
                  captionHTML = hiddenCaption.innerHTML
                } else {
                  // get caption from alt attribute
                  captionHTML =
                    currSlideElement
                      ?.querySelector('img')
                      ?.getAttribute('alt') || ''
                }
              }
              el.innerHTML = captionHTML || ''
            })
          }
        })
      })

      lightbox.on('contentLoad', (e) => {
        const { content } = e
        if (content.type === 'iframe') {
          e.preventDefault()
          content.element = document.createElement('div')
          content.element.className = 'pswp__iframe-wrap'
          content.element.style.cssText =
            'width:100%;height:100%;display:flex;align-items:center;justify-content:center;'

          const iframe = document.createElement('iframe')
          iframe.src = (content.data as { src: string }).src
          iframe.setAttribute('allowfullscreen', '')
          iframe.style.cssText = 'width:100%;border:none;aspect-ratio:16/9;'
          content.element.appendChild(iframe)
          content.onLoaded()
        }
      })

      lightbox.on('contentDeactivate', ({ content }) => {
        if (content.type === 'iframe' && content.element) {
          const iframe = content.element.querySelector('iframe')
          if (iframe) {
            iframe.src = ''
          }
        }
      })

      lightbox.init()

      lightboxes.push(lightbox)
    })

    return () => {
      lightboxes.length > 0 &&
        lightboxes.forEach((lightbox: PhotoSwipeLightbox) => {
          lightbox.destroy()
          lightbox = null as unknown as PhotoSwipeLightbox
        })
      lightboxes = []
    }
  }, [])

  return (
    <Section data={data} anim="fade-in" className={'s-section-webcams'}>
      <SectionHeader
        title={c?.title}
        titleLayout={c?.titleLayout}
        overline={c?.overline}
      />
      {total && total.length > 0 && (
        <Container className="relative">
          <div
            id={`gallery-gallery`}
            className="gap-xs mt-md grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {total?.map((item) => (
              <a
                key={item.uid}
                href={
                  item.hasExternalLink && item.externalLink
                    ? item.externalLink
                    : item.images?.default || undefined
                }
                data-pswp-type={
                  item.panorama || (item.roundshot && item.hasExternalLink)
                    ? 'iframe'
                    : undefined
                }
                data-pswp-width={!item.externalLink && item.dimensions?.width}
                data-pswp-height={!item.externalLink && item.dimensions?.height}
                rel="noreferrer"
                target="_blank"
              >
                <Suspense fallback={<div>Loading...</div>}>
                  <CardWebcam data={item} />
                </Suspense>
              </a>
            ))}
          </div>
        </Container>
      )}
    </Section>
  )
}

SectionWebcams.displayName = 'SectionWebcams'

export default memo(SectionWebcams)
